import React, { useState, useRef, useEffect } from "react";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { useTranslation } from "../../i18n/TranslationProvider";

const GlobalSearchBar = ({ userType, patientList, caregiverList, onSelect }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const trimmed = query.trim().toLowerCase();

  // Build unified results from both lists, deduplicating by patientId
  const results = (() => {
    if (!trimmed) return [];

    const seen = new Set();
    const matches = [];

    // Direct patients
    (patientList || []).forEach((p) => {
      const name = `${p.firstName} ${p.lastName}`.toLowerCase();
      const externalId = p.externalId ? String(p.externalId).toLowerCase() : "";
      if ((name.includes(trimmed) || externalId.includes(trimmed)) && !seen.has(p.patientId)) {
        seen.add(p.patientId);
        matches.push({
          type: "patient",
          data: p,
          label: `${p.firstName} ${p.lastName}`,
          sublabel: p.externalId ? `ID: ${p.externalId}` : null,
          badge: p.patientStatus === "Released" ? "Released" : null,
          incomplete: p.incompletedRegister || false,
        });
      }
    });

    // Caregiver-linked patients (caregiverList is flat caregiver-patient pairs)
    (caregiverList || []).forEach((c) => {
      const patientName = `${c.firstName} ${c.lastName}`.toLowerCase();
      const caregiverName = (c.caregiverName || "").toLowerCase();
      const externalId = c.externalId ? String(c.externalId).toLowerCase() : "";

      const matchesPatient = patientName.includes(trimmed) || externalId.includes(trimmed);
      const matchesCaregiver = caregiverName.includes(trimmed);

      if ((matchesPatient || matchesCaregiver) && !seen.has(c.patientId)) {
        seen.add(c.patientId);
        matches.push({
          type: "caregiver_patient",
          data: c,
          label: `${c.firstName} ${c.lastName}`,
          sublabel: c.caregiverName || null,
          badge: c.patientStatus === "Released" ? "Released" : null,
          incomplete: c.incompletedRegister || false,
        });
      }
    });

    return matches.sort((a, b) => a.label.localeCompare(b.label));
  })();

  const handleSelect = (result) => {
    onSelect(result.data, result.type);
    setQuery("");
    setShowDropdown(false);
  };

  const handleChange = (e) => {
    setQuery(e.target.value);
    setShowDropdown(true);
  };

  return (
    <Row className="d-flex justify-content-center mb-4">
      <Col md={6}>
        <div ref={containerRef} style={{ position: "relative" }}>
          {/* Input */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              border: `1px solid ${focused ? "#3eb1a6" : "#cccccc"}`,
              borderRadius: showDropdown && results.length > 0 ? "4px 4px 0 0" : "4px",
              backgroundColor: "#fff",
              boxShadow: "none",
              transition: "border-color 0.2s ease",
            }}
          >
            <input
              type="text"
              value={query}
              onChange={handleChange}
              onFocus={() => {
                setFocused(true);
                if (query.trim()) setShowDropdown(true);
              }}
              onBlur={() => setFocused(false)}
              placeholder={t("dashboard.patientControl.searchPatientsOrCaregivers")}
              style={{
                flex: 1,
                padding: "9px 12px 9px 14px",
                fontSize: "0.95rem",
                border: "none",
                outline: "none",
                borderRadius: "4px",
                color: "#495057",
                backgroundColor: "transparent",
              }}
            />
            {/* Clear button */}
            {query && (
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  setQuery("");
                  setShowDropdown(false);
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "0 6px",
                  color: "#adb5bd",
                  fontSize: "1rem",
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            )}
            {/* Search icon */}
            <div
              style={{
                padding: "0 12px",
                display: "flex",
                alignItems: "center",
                color: focused ? "#3eb1a6" : "#adb5bd",
                transition: "color 0.2s ease",
                flexShrink: 0,
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
          </div>

          {/* Dropdown results */}
          {showDropdown && trimmed && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                backgroundColor: "#fff",
                border: "1.5px solid #3eb1a6",
                borderTop: "none",
                borderRadius: "0 0 4px 4px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                zIndex: 1000,
                maxHeight: "260px",
                overflowY: "auto",
              }}
            >
              {results.length === 0 ? (
                <div
                  style={{
                    padding: "12px 14px",
                    color: "#6c757d",
                    fontSize: "0.9rem",
                  }}
                >
                  {t("dashboard.patientControl.globalSearch.noResults")}
                </div>
              ) : (
                results.map((result, idx) => (
                  <div
                    key={`${result.type}-${result.data.patientId}-${idx}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(result);
                    }}
                    style={{
                      padding: "10px 14px",
                      cursor: result.incomplete ? "not-allowed" : "pointer",
                      borderBottom: idx < results.length - 1 ? "1px solid #f0f0f0" : "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      opacity: result.incomplete ? 0.55 : 1,
                      backgroundColor: "#fff",
                    }}
                    onMouseEnter={(e) => {
                      if (!result.incomplete)
                        e.currentTarget.style.backgroundColor = "#f0fbfa";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#fff";
                    }}
                  >
                    <div>
                      <div style={{ fontSize: "0.95rem", color: "#212529", fontWeight: 500 }}>
                        {result.label}
                      </div>
                      {result.sublabel && (
                        <div style={{ fontSize: "0.8rem", color: "#6c757d", marginTop: 2 }}>
                          {result.type === "caregiver_patient"
                            ? t("dashboard.patientControl.globalSearch.viaCaregiver", { name: result.sublabel })
                            : result.sublabel}
                        </div>
                      )}
                      {result.incomplete && (
                        <div style={{ fontSize: "0.75rem", color: "#e67e22", marginTop: 2 }}>
                          {t("dashboard.patientControl.globalSearch.incompleteRegistration")}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                      {result.badge && (
                        <span
                          style={{
                            fontSize: "0.75rem",
                            backgroundColor: "#6c757d",
                            color: "#fff",
                            borderRadius: 4,
                            padding: "2px 6px",
                          }}
                        >
                          {t("dashboard.ui.released")}
                        </span>
                      )}
                      <span
                        style={{
                          fontSize: "0.72rem",
                          backgroundColor:
                            result.type === "caregiver_patient" ? "#e8f4f8" : "#e8f8f5",
                          color:
                            result.type === "caregiver_patient" ? "#2980b9" : "#27ae60",
                          borderRadius: 4,
                          padding: "2px 6px",
                          fontWeight: 500,
                        }}
                      >
                        {result.type === "caregiver_patient"
                            ? t("dashboard.patientControl.globalSearch.typeCaregiverPatient")
                            : t("dashboard.patientControl.globalSearch.typePatient")}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default GlobalSearchBar;